import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useSearchParams, useParams, useLocation } from 'react-router-dom';
import { adminApi } from '../../services/api';
import SidebarPanel from './SidebarPanel';
import RetellCallViewer from '../sales/RetellCallViewer';
import AddActivityModal from './AddActivityModal';
import ActivityTimeline from '../shared/ActivityTimeline';
import AlertMessage from '../shared/alertMessage';
import VoiceAITranscriptViewer from '../shared/VoiceAITranscriptViewer';
import { useIsAnkrAdmin } from '../../context/AuthContext';
import InlineFeedback from '../shared/InlineFeedback';
import FieldValidator from '../shared/FieldValidator';
import FeedbackTriggerService from '../../services/FeedbackTriggerService';
import { usePostHog, POSTHOG_EVENTS } from '../analytics/PostHogProvider';
import { setActivitiesData, addActivity, setSelectedEventId,
  setLoading as setActivitiesLoading,
} from '../redux/slices/vcxEventActivitiesSlice';
import { updateEvent, setSelectedEvent } from '../redux/slices/vcxEventsSlice';

const VALID_TABS = ['summary', 'activities', 'transcript', 'channel-data', 'raw'];

const CALL_DOMAIN_OPTIONS = [
  { value: '', label: 'None' },
  { value: 'appointments', label: '📅 Appointments' },
  { value: 'medications', label: '💊 Medications' },
  { value: 'medicalIssues', label: '🏥 Medical Issues' },
  { value: 'billingInsurance', label: '💳 Billing/Insurance' },
  { value: 'medicalRecords', label: '📋 Medical Records' },
  { value: 'new_patient', label: '🆕 New Patient' },
  { value: 'generalInquiries', label: 'ℹ️ General Inquiries' },
  { value: 'providerCalls', label: '👨‍⚕️ Provider Calls' },
  { value: 'vendorCalls', label: '📞 Vendor Calls' },
  { value: 'unknown', label: '❓ Unknown' }
];

// EditableField component defined outside to prevent recreation on every render
const EditableField = ({ label, field, type = 'text', options = null, value, isEditing, editedData, handleFieldChange, description = null, disabled = false }) => {
  // Handle nested field access (e.g., "event_metadata.priority")
  const getEditedValue = () => {
    if (!isEditing) return value;

    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      return editedData[parent]?.[child] ?? value;
    }
    return editedData[field] ?? value;
  };

  const displayValue = getEditedValue();

  if (!isEditing) {
    // View mode
    if (type === 'select' && options) {
      const option = options.find(opt => opt.value === displayValue);
      return (
        <p className="mb-2">
          <strong>{label}:</strong> {option?.label || displayValue || 'N/A'}
        </p>
      );
    }
    if (type === 'checkbox') {
      return (
        <p className="mb-2">
          <strong>{label}:</strong>{' '}
          <span className={`badge bg-${displayValue ? 'warning' : 'secondary'}`}>
            {displayValue ? 'Yes' : 'No'}
          </span>
        </p>
      );
    }
    return (
      <p className="mb-2">
        <strong>{label}:</strong> {displayValue || 'N/A'}
      </p>
    );
  }

  // Edit mode
  if (type === 'select' && options) {
    return (
      <div className="mb-2">
        <label className="form-label fw-bold mb-1">{label}</label>
        <select
          className="form-select form-select-sm"
          value={displayValue || ''}
          onChange={(e) => handleFieldChange(field, e.target.value)}
          disabled={disabled}
          style={disabled ? {} : { backgroundColor: 'white' }}
        >
          {options.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        {description && <small className="text-muted d-block">{description}</small>}
      </div>
    );
  }

  if (type === 'checkbox') {
    return (
      <div className="mb-2 form-check">
        <input
          type="checkbox"
          className="form-check-input"
          id={`edit-${field}`}
          checked={displayValue}
          onChange={(e) => handleFieldChange(field, e.target.checked)}
          disabled={disabled}
        />
        <label className="form-check-label fw-bold" htmlFor={`edit-${field}`}>
          {label}
        </label>
        {description && <small className="text-muted d-block ms-4">{description}</small>}
      </div>
    );
  }

  return (
    <div className="mb-2">
      <label className="form-label fw-bold mb-1">{label}</label>
      <input
        type={type}
        className="form-control form-control-sm"
        value={displayValue || ''}
        onChange={(e) => handleFieldChange(field, e.target.value)}
        disabled={disabled}
        style={disabled ? {} : { backgroundColor: 'white' }}
      />
      {description && <small className="text-muted d-block">{description}</small>}
    </div>
  );
};

const VCXEventDetailPanel = ({
  event: propEvent,
  isOpen: propIsOpen,
  onClose: propOnClose,
  healthSystemId: propHealthSystemId,
  onUpdate,
  openInEditMode: propOpenInEditMode = false,
  onEditStart,
  onEditEnd,
  openActivityModal = false,
  onNavigate = null,
  currentIndex = -1,
  totalEvents = 0
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { domain, eventId: paramEventId, tab: paramTab } = useParams();
  const isAnkrAdmin = useIsAnkrAdmin();
  const { captureEvent } = usePostHog();

  // Standalone mode: detect /edit from URL, Sidebar mode: use prop
  const openInEditMode = paramEventId
    ? location.pathname.includes('/edit')
    : propOpenInEditMode;

  const activitiesData = useSelector((state) => state.vcxEventActivities.activitiesData);
  const activitiesLoading = useSelector((state) => state.vcxEventActivities.loading);
  const currentUser = useSelector((state) => state.user.currentUser);

  // Read event from Redux (single source of truth)
  const reduxEvent = useSelector((state) => state.vcxEvents.selectedEvent);

  // Support both prop-based and URL param-based event access
  const [fetchedEvent, setFetchedEvent] = useState(null);
  const [loadingEvent, setLoadingEvent] = useState(false);
  const [eventError, setEventError] = useState(null);

  // Use event from Redux first (after save), fallback to prop or fetched
  const event = reduxEvent || propEvent || fetchedEvent;

  // When used standalone via URL params, always open
  const isOpen = propIsOpen !== undefined ? propIsOpen : !!paramEventId;
  // eslint-disable-next-line no-unused-vars
  const healthSystemId = propHealthSystemId || event?.health_system;

  // Close handler - navigate back if used standalone, otherwise call prop onClose
  const onClose = propOnClose || (() => navigate(-1));

  // Extract tab from URL path params or search params (fallback)
  const urlTab = paramTab || searchParams.get('tab');
  const initialTab = VALID_TABS.includes(urlTab) ? urlTab : 'summary';

  const [activeTab, setActiveTab] = useState(initialTab);
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [shouldPulseActivity, setShouldPulseActivity] = useState(false);
  const [copied, setCopied] = useState(false);

  // Call viewer state
  const [viewerOpen, setViewerOpen] = useState(false);
  const [selectedCallId, setSelectedCallId] = useState(null);

  // Inline editing state
  const [isEditing, setIsEditing] = useState(false);
  const [editedData, setEditedData] = useState({});
  const [saving, setSaving] = useState(false);

  // Health systems list for dropdown
  const [healthSystems, setHealthSystems] = useState([]);
  const [loadingHealthSystems, setLoadingHealthSystems] = useState(false);

  // Users list for assigned_to dropdown
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Alert state for notifications
  const [alertState, setAlertState] = useState({ show: false, type: 'info', title: '', message: '' });

  // Feedback state - batched for performance
  const [batchedFeedback, setBatchedFeedback] = useState({});
  const feedbackChangedRef = useRef(false);

  // Navigation debounce state
  const [isNavigating, setIsNavigating] = useState(false);
  const navigateTimerRef = useRef(null);

  useEffect(() => {
    return () => clearTimeout(navigateTimerRef.current);
  }, []);

  // Fetch event when accessed via URL params (standalone mode)
  useEffect(() => {
    let cancelled = false;
    if (paramEventId && !propEvent) {
      const fetchEvent = async () => {
        setLoadingEvent(true);
        setEventError(null);
        try {
          const response = await adminApi.getVCXEvent(paramEventId);
          if (cancelled) return;
          if (response.data) {
            setFetchedEvent(response.data);
          } else {
            setEventError('Event not found');
          }
        } catch (err) {
          if (cancelled) return;
          setEventError(err.response?.data?.error || err.message || 'Failed to fetch event');
        } finally {
          if (!cancelled) setLoadingEvent(false);
        }
      };
      fetchEvent();
    }
    return () => { cancelled = true; };
  }, [paramEventId, propEvent]);

  // Sync activeTab with URL path param changes
  useEffect(() => {
    if (VALID_TABS.includes(paramTab) && paramTab !== activeTab) {
      setActiveTab(paramTab);
    } else if (!paramTab && activeTab !== 'summary') {
      setActiveTab('summary');
    }
  }, [paramTab, activeTab]);

  // Handle openActivityModal prop - open activity modal when requested
  useEffect(() => {
    if (isOpen && openActivityModal && event) {
      setShowActivityModal(true);
      setShouldPulseActivity(true);
    }
  }, [isOpen, openActivityModal, event]);

  // Sync edit state with URL
  useEffect(() => {
    if (isOpen && event) {
      setIsEditing(openInEditMode);
    } else if (!isOpen) {
      // Reset editing state when panel closes
      setIsEditing(false);
      setEditedData({});
      setAlertState({ show: false, type: 'info', title: '', message: '' });
    }
  }, [isOpen, openInEditMode, event]);

  // Lazy load event_notes if missing from cached event (list API excludes it for performance)
  useEffect(() => {
    let cancelled = false;
    if (event && isOpen && !event.event_notes) {
      const eventId = event.id;
      const fetchSummary = async () => {
        try {
          const response = await adminApi.getVCXEventSummary(eventId);
          if (cancelled) return;
          if (response.data) {
            // Merge event_notes into existing event and update Redux
            const updatedEvent = {
              ...event,
              event_notes: response.data.event_notes
            };
            dispatch(setSelectedEvent(updatedEvent));
          }
        } catch (error) {
          if (cancelled) return;
          console.error('Failed to load event summary:', error);
        }
      };
      fetchSummary();
    }
    return () => { cancelled = true; };
  }, [event, isOpen, dispatch]);

  const copyToClipboard = (text, label = 'Text') => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(label);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  // Initialize edited data when event changes or editing starts
  useEffect(() => {
    if (event && isEditing) {
      setEditedData({
        channel: event.channel || '',
        direction: event.direction || '',
        customer_name: event.customer_name || '',
        customer_phone: event.customer_phone || '',
        customer_email: event.customer_email || '',
        customer_type: event.customer_type || '',
        call_domain: event.call_domain || '',
        caller_intent: event.caller_intent || '',
        action: event.action || '',
        outcome: event.outcome || '',
        follow_up_required: event.follow_up_required || false,
        // Related entities (only editable ones)
        health_system: event.health_system || '',
        // Use nested object for event_metadata instead of dotted keys
        event_metadata: {
          assigned_to: event.event_metadata?.assigned_to || '',
          assigned_to_name: event.event_metadata?.assigned_to_name || null,
          priority: event.event_metadata?.priority || '',
          caller_data: event.event_metadata?.caller_data || {
            caller_first_name: event.event_metadata?.caller_data?.caller_first_name || event.extracted_patient_data?.caller_first_name || '',
            caller_last_name: event.event_metadata?.caller_data?.caller_last_name || event.extracted_patient_data?.caller_last_name || '',
            caller_name: event.event_metadata?.caller_data?.caller_name || event.extracted_patient_data?.caller_name || '',
          },
          patient_data: event.event_metadata?.patient_data || {
            patient_first_name: event.event_metadata?.patient_data?.patient_first_name || event.extracted_patient_data?.patient_first_name || '',
            patient_last_name: event.event_metadata?.patient_data?.patient_last_name || event.extracted_patient_data?.patient_last_name || '',
            patient_dob: event.event_metadata?.patient_data?.patient_dob || event.extracted_patient_data?.patient_dob || '',
            match_patient_id: event.event_metadata?.patient_data?.match_patient_id || event.extracted_patient_data?.match_patient_id || '',
          },
        },
        // Note: patient, provider, mirasolcase, voiceai_agent, performed_by are read-only
      });
    }
  }, [event, isEditing]);

  // Fetch health systems and users when editing starts (only when panel is open)
  useEffect(() => {
    if (isEditing && isOpen) {
      // Fetch health systems (admin only)
      if (isAnkrAdmin) {
        const fetchHealthSystems = async () => {
          setLoadingHealthSystems(true);
          try {
            const response = await adminApi.getHealthSystems();
            const systems = response.data.health_systems || response.data.results || response.data || [];
            // Ensure it's always an array
            setHealthSystems(Array.isArray(systems) ? systems : []);
          } catch (error) {
            setAlertState({
              show: true, type: 'error', title: 'Failed to Load Health Systems',
              message: error.response?.data?.message || ''
            });
            setHealthSystems([]); // Set empty array on error
          } finally {
            setLoadingHealthSystems(false);
          }
        };
        fetchHealthSystems();
      }

      // Fetch users for assigned_to dropdown
      const fetchUsers = async () => {
        setLoadingUsers(true);
        try {
          const response = await adminApi.getUsers({ page_size: 1000, response_format: 'minimal' });
          const usersData = response.data?.users || response.data?.results || response.data || [];

          // Filter to only staff users (ankr and healthcare), exclude patients
          let staffUsers = Array.isArray(usersData)
            ? usersData.filter(user => user.user_type === 'ankr' || user.user_type === 'healthcare')
            : [];

          // Sort alphabetically by lastname, firstname within each user type
          staffUsers = staffUsers.sort((a, b) => {
            // First sort by user type (ankr first, then healthcare)
            if (a.user_type !== b.user_type) {
              return a.user_type === 'ankr' ? -1 : 1;
            }

            // Within same user type, sort by lastname, then firstname
            const lastNameA = (a.last_name || '').toLowerCase();
            const lastNameB = (b.last_name || '').toLowerCase();
            const firstNameA = (a.first_name || '').toLowerCase();
            const firstNameB = (b.first_name || '').toLowerCase();

            if (lastNameA !== lastNameB) {
              return lastNameA.localeCompare(lastNameB);
            }
            return firstNameA.localeCompare(firstNameB);
          });

          setUsers(staffUsers);
        } catch (error) {
          console.error('Failed to load users:', error);
          setUsers([]);
        } finally {
          setLoadingUsers(false);
        }
      };
      fetchUsers();
    }
  }, [isEditing, isOpen, isAnkrAdmin]);

  // Auto-dismiss success alert messages after 5 seconds
  useEffect(() => {
    if (alertState.show && alertState.type === 'success') {
      const timer = setTimeout(() => {
        setAlertState(prev => ({ ...prev, show: false }));
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [alertState.show, alertState.type]);

  const handleEditClick = () => {
    setAlertState({ show: false, type: 'info', title: '', message: '' });

    if (paramEventId) {
      // Standalone mode - navigate to /edit URL
      const basePath = domain ? `/admin/vcx-events/domain/${domain}/${paramEventId}` : `/admin/vcx-events/${paramEventId}`;
      const newPath = paramTab ? `${basePath}/edit/${paramTab}` : `${basePath}/edit`;
      navigate(newPath, { replace: true });
    } else {
      // Sidebar mode - notify parent
      onEditStart?.();
    }
  };

  const handleCancelEdit = () => {
    setEditedData({});
    setAlertState({ show: false, type: 'info', title: '', message: '' });

    if (paramEventId) {
      // Standalone mode - navigate back to non-edit URL
      const basePath = domain ? `/admin/vcx-events/domain/${domain}/${paramEventId}` : `/admin/vcx-events/${paramEventId}`;
      const newPath = paramTab ? `${basePath}/${paramTab}` : basePath;
      navigate(newPath, { replace: true });
    } else {
      // Sidebar mode - notify parent
      onEditEnd?.();
    }
  };

  const handleArchiveEvent = async () => {
    const confirmMessage = `Are you sure you want to archive this event?\n\nThis will hide the event from the active list but can be restored later.\n\nCustomer: ${event.customer_name || event.patient_name || 'N/A'}\nChannel: ${event.channel}\nCreated: ${new Date(event.created_at).toLocaleString()}`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      // Use auto-scoped endpoint
      await adminApi.deleteVCXEvent(event.id, false);

      setAlertState({ show: true, type: 'success', title: 'Event Archived',
        message: 'Event has been successfully archived'
      });

      setTimeout(() => {
        onClose();
        if (onUpdate) {
          onUpdate(null);
        }
      }, 1500);
    } catch (err) {
      setAlertState({ show: true, type: 'error', title: 'Archive Failed',
        message: err.response?.data?.error || 'Failed to archive event'
      });
    }
  };

  const handlePermanentDelete = async () => {
    const confirmation = window.prompt(
      `⚠️ WARNING: PERMANENT DELETION ⚠️\n\n` +
      `This action CANNOT be undone! The event will be permanently removed from the database.\n\n` +
      `Note: Activities will be preserved for audit trail.\n\n` +
      `Customer: ${event.customer_name || event.patient_name || 'N/A'}\n` +
      `Channel: ${event.channel}\n` +
      `Created: ${new Date(event.created_at).toLocaleString()}\n\n` +
      `Type DELETE to confirm:`
    );

    if (confirmation !== 'DELETE') {
      if (confirmation !== null) {
        setAlertState({
          show: true, type: 'info', title: 'Deletion Cancelled', message: 'You must type DELETE to confirm permanent deletion'
        });
      }
      return;
    }

    try {
      // Use auto-scoped endpoint
      await adminApi.deleteVCXEvent(event.id, true);

      setAlertState({ show: true, type: 'success', title: 'Event Permanently Deleted',
        message: 'Event has been permanently removed. Activities preserved for audit trail.'
      });
      setTimeout(() => {
        onClose();
        if (onUpdate) {
          onUpdate(null);
        }
      }, 1500);
    } catch (err) {
      setAlertState({ show: true, type: 'error', title: 'Delete Failed',
        message: err.response?.data?.error || 'Failed to permanently delete event'
      });
    }
  };

  const handleRestoreEvent = async () => {
    const confirmMessage = `Restore this archived event?\n\nThis will make the event visible in the active list again.\n\nCustomer: ${event.customer_name || event.patient_name || 'N/A'}\nChannel: ${event.channel}\nCreated: ${new Date(event.created_at).toLocaleString()}`;
    if (!window.confirm(confirmMessage)) {
      return;
    }
    try {
      await adminApi.restoreVCXEvent(event.id);
      setAlertState({ show: true, type: 'success', title: 'Event Restored', message: 'Event has been restored and is now active' });
      setTimeout(() => {
        if (onUpdate) {
          onUpdate({ ...event, is_active: true });
        }
      }, 1500);
    } catch (err) {
      setAlertState({ show: true, type: 'error', title: 'Restore Failed', message: err.response?.data?.error || 'Something went wrong. Please refresh the page and try again later.' });
    }
  };

  // Helper to get user display name
  const getUserDisplayName = (user) => {
    return user.first_name && user.last_name
      ? `${user.first_name} ${user.last_name}`
      : user.email || user.username;
  };

  const handleFieldChange = (field, value) => {
    // Handle nested fields like "event_metadata.priority"
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setEditedData(prev => ({
        ...prev,
        [parent]: {
          ...(prev[parent] || {}),
          [child]: value
        }
      }));
    } else {
      setEditedData(prev => ({ ...prev, [field]: value }));
    }
  };

  // Helper to normalize values (treat null, undefined, and empty string as equivalent)
  const normalizeValue = (val) => {
    if (val === null || val === undefined || val === '') return null;
    return val;
  };

  // Memoized computation of changed fields
  const changedData = useMemo(() => {
    const changes = {};

    Object.keys(editedData).forEach(key => {
      if (key === 'event_metadata') {
        const originalMetadata = event.event_metadata || {};
        const editedMetadata = editedData.event_metadata || {};
        const changedMetadata = {};

        Object.keys(editedMetadata).forEach(metaKey => {
          // Handle nested patient_data object
          if (metaKey === 'patient_data' && typeof editedMetadata[metaKey] === 'object') {
            const originalPatientData = originalMetadata.patient_data || {};
            const editedPatientData = editedMetadata.patient_data || {};
            const changedPatientData = {};

            Object.keys(editedPatientData).forEach(patientKey => {
              const normalizedOld = normalizeValue(originalPatientData[patientKey]);
              const normalizedNew = normalizeValue(editedPatientData[patientKey]);
              if (normalizedNew !== normalizedOld) {
                changedPatientData[patientKey] = editedPatientData[patientKey];
              }
            });

            if (Object.keys(changedPatientData).length > 0) {
              changedMetadata.patient_data = changedPatientData;
            }
          } else if (metaKey === 'caller_data' && typeof editedMetadata[metaKey] === 'object') {
            // Handle nested caller_data object (independent from patient_data)
            const originalCallerData = originalMetadata.caller_data || {};
            const editedCallerData = editedMetadata.caller_data || {};
            const changedCallerData = {};

            Object.keys(editedCallerData).forEach(callerKey => {
              const normalizedOld = normalizeValue(originalCallerData[callerKey]);
              const normalizedNew = normalizeValue(editedCallerData[callerKey]);
              if (normalizedNew !== normalizedOld) {
                changedCallerData[callerKey] = editedCallerData[callerKey];
              }
            });

            if (Object.keys(changedCallerData).length > 0) {
              changedMetadata.caller_data = changedCallerData;
            }
          } else {
            const normalizedOld = normalizeValue(originalMetadata[metaKey]);
            const normalizedNew = normalizeValue(editedMetadata[metaKey]);
            if (normalizedNew !== normalizedOld) {
              changedMetadata[metaKey] = editedMetadata[metaKey];
            }
          }
        });

        // If assigned_to changed, always include assigned_to_name for display purposes
        if ('assigned_to' in changedMetadata && 'assigned_to_name' in editedMetadata) {
          changedMetadata.assigned_to_name = editedMetadata.assigned_to_name;
        }

        if (Object.keys(changedMetadata).length > 0) {
          changes.event_metadata = changedMetadata;
        }
      } else {
        const normalizedOld = normalizeValue(event[key]);
        const normalizedNew = normalizeValue(editedData[key]);
        if (normalizedNew !== normalizedOld) {
          changes[key] = editedData[key];
        }
      }
    });

    return changes;
  }, [event, editedData]);

  // Quick update function for single field changes (buttons)
  const handleQuickUpdate = async (fieldUpdates) => {
    setSaving(true);
    setAlertState({ show: false, type: 'info', title: '', message: '' });

    try {
      const response = await adminApi.updateVCXEvent(event.id, fieldUpdates);

      // Update Redux events list and selected event immediately
      dispatch(updateEvent(response.data));
      dispatch(setSelectedEvent(response.data));
      await fetchActivities();

      // Scroll to top to show alert
      window.scrollTo({ top: 0, behavior: 'smooth' });

      // Show success alert
      setAlertState({ show: true, type: 'success', title: '',
        message: 'Event successfully updated!'
      });
    } catch (err) {
      // Scroll to top to show error
      window.scrollTo({ top: 0, behavior: 'smooth' });

      setAlertState({ show: true, type: 'error', title: 'Update Failed',
        message: err.response?.data?.detail || err.response?.data?.message || 'Please try again in a minute.'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEdit = async () => {

    setSaving(true);
    setAlertState({ show: false, type: 'info', title: '', message: '' });

    try {
      // Only send the request if there are actual changes
      if (Object.keys(changedData).length === 0) {
        setIsEditing(false);
        setEditedData({});
        setSaving(false);
        return;
      }

      // Check if call_domain changed (event will leave current filtered view)
      const domainChanged = changedData.call_domain && changedData.call_domain !== event.call_domain;
      const newDomain = changedData.call_domain;

      // Use auto-scoped endpoint
      const response = await adminApi.updateVCXEvent(event.id, changedData);

      // If domain changed, close panel and show notification (skip refetch of event list)
      if (domainChanged) {
        const domainOption = CALL_DOMAIN_OPTIONS.find(opt => opt.value === newDomain);
        const domainLabel = domainOption?.label || newDomain;

        // Refresh activities to show the domain change activity created by backend
        await fetchActivities();

        // Scroll to top to show alert
        window.scrollTo({ top: 0, behavior: 'smooth' });

        setAlertState({
          show: true,
          type: 'success',
          title: `Event moved to ${domainLabel}`,
          message: `This event is no longer in the current filtered view. Please refresh the table after you close this panel.`
        });

        setIsEditing(false);
        setEditedData({});

        // Don't call onUpdate - skip event list refetch since event left filtered view
        return;
      }

      // Normal update flow (domain didn't change)

      // Update Redux events list and selected event immediately
      dispatch(updateEvent(response.data));
      dispatch(setSelectedEvent(response.data));
      await fetchActivities();

      // Scroll to top to show alert
      window.scrollTo({ top: 0, behavior: 'smooth' });

      // Show success alert
      setAlertState({ show: true, type: 'success', title: '',
        message: 'Event successfully updated!'
      });

      // Exit edit mode
      setEditedData({});

      if (paramEventId) {
        // Standalone mode - navigate back to non-edit URL
        const basePath = domain ? `/admin/vcx-events/domain/${domain}/${paramEventId}` : `/admin/vcx-events/${paramEventId}`;
        const newPath = paramTab ? `${basePath}/${paramTab}` : basePath;
        navigate(newPath, { replace: true });
      } else {
        // Sidebar mode - notify parent
        onEditEnd?.();
      }
    } catch (err) {
      // Scroll to top to show error
      window.scrollTo({ top: 0, behavior: 'smooth' });

      setAlertState({ show: true, type: 'error', title: 'Update Failed',
        message: err.response?.data?.detail || err.response?.data?.message || 'Please try again in a minute.'
      });
    } finally {
      setSaving(false);
    }
  };

  // Feedback handlers - BATCHED (no API calls until panel closes)
  const handleInlineFeedbackSubmit = (category, value) => {
    setBatchedFeedback(prev => ({
      ...prev,
      [category]: value,
      [`${category}_submitted_at`]: new Date().toISOString(),
      [`${category}_submitted_by`]: currentUser?.djangoUserId
    }));
    feedbackChangedRef.current = true;

    // Track immediately with PostHog (async, non-blocking)
    if (captureEvent) {
      captureEvent(POSTHOG_EVENTS.FEEDBACK_INLINE_SUBMITTED, {
        event_id: event.id,
        category,
        value
      });
    }
  };

  const handleFieldValidation = (fieldName, isCorrect) => {
    setBatchedFeedback(prev => ({
      ...prev,
      patient_data_validation: {
        ...(prev.patient_data_validation || {}),
        [fieldName]: isCorrect
      }
    }));
    feedbackChangedRef.current = true;

    // Track immediately with PostHog (async, non-blocking)
    if (captureEvent) {
      captureEvent(POSTHOG_EVENTS.FEEDBACK_FIELD_VALIDATED, {
        event_id: event.id,
        field_name: fieldName,
        is_correct: isCorrect
      });
    }
  };

  // Submit batched feedback - creates activity only (no event metadata update)
  const submitBatchedFeedback = useCallback(async () => {
    if (!feedbackChangedRef.current || !event?.id || Object.keys(batchedFeedback).length === 0) {
      return;
    }

    try {
      const feedbackUpdate = {
        event_metadata: {
          ...event.event_metadata,
          feedback: {
            ...(event.event_metadata?.feedback || {}),
            ...batchedFeedback
          }
        }
      };

      // Create a custom activity for inline feedback submission
      const feedbackCategories = Object.keys(batchedFeedback).filter(key => !key.includes('_submitted_'));
      if (feedbackCategories.length > 0) {
        await adminApi.createVCXEventActivity(event.id, {
          activity_type: 'QUICK_FEEDBACK_SUBMITTED',
          action: 'Quick Feedback Submitted',
          notes: `User provided quick feedback on: ${feedbackCategories.join(', ')}`,
          activity_metadata: {
            feedback_categories: feedbackCategories,
            feedback_data: batchedFeedback
          }
        });
      }

      // Update Redux after successful submit
      dispatch(updateEvent({ ...event, ...feedbackUpdate }));
      dispatch(setSelectedEvent({ ...event, ...feedbackUpdate }));

      // Reset batch
      setBatchedFeedback({});
      feedbackChangedRef.current = false;
    } catch (error) {
      console.error('Failed to submit batched feedback:', error);
    }
  }, [event, batchedFeedback, dispatch]);

  const fetchActivities = useCallback(async () => {
    if (!event?.id) return;

    dispatch(setActivitiesLoading(true));
    try {
      const response = await adminApi.getVCXEventActivities(event.id);
      if (response.data) {
        dispatch(setActivitiesData(response.data.results || []));
      }
    } catch (error) {
      console.error('Failed to fetch activities:', error);
    } finally {
      dispatch(setActivitiesLoading(false));
    }
  }, [event?.id, dispatch]);

  const fetchActivityWithPayload = useCallback(async (activityId) => {
    if (!event) return null;

    try {
      const response = await adminApi.getVCXEventActivity(
        event.id,
        activityId,
        { response_format: 'full' }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to fetch activity with webhook payload:', error);
      throw error;
    }
  }, [event]);

  // Clear activities immediately when event changes to prevent showing stale data
  useEffect(() => {
    if (event?.id && isOpen) {
      dispatch(setActivitiesData([])); // Clear old activities immediately
      dispatch(setSelectedEventId(event.id));
    }
  }, [event?.id, isOpen, dispatch]);

  // Fetch activities when event changes
  useEffect(() => {
    if (event?.id && isOpen) {
      fetchActivities();
    }
  }, [event?.id, isOpen, fetchActivities]);

  // Reset batched feedback when event changes
  useEffect(() => {
    if (event?.id) {
      setBatchedFeedback({});
      feedbackChangedRef.current = false;
    }
  }, [event?.id]);

  // Submit batched feedback when event changes or component unmounts
  useEffect(() => {
    return () => {
      // Cleanup: submit any pending feedback
      if (feedbackChangedRef.current && event?.id && Object.keys(batchedFeedback).length > 0) {
        submitBatchedFeedback();
      }
    };
  }, [event?.id, batchedFeedback, submitBatchedFeedback]);

  // Submit batched feedback when panel closes
  useEffect(() => {
    if (!isOpen && feedbackChangedRef.current) {
      submitBatchedFeedback();
    }
  }, [isOpen, submitBatchedFeedback]);

  // Track event review for session analytics
  useEffect(() => {
    if (event?.id && isOpen) {
      FeedbackTriggerService.trackEventReview(captureEvent ? { captureEvent } : null);
    }
  }, [event?.id, isOpen, captureEvent]);

  useEffect(() => {
    if (!event?.id || !isOpen || !paramEventId) return;

    const currentPath = window.location.pathname;
    const basePath = domain
      ? `/admin/vcx-events/domain/${domain}/${event.id}`
      : `/admin/vcx-events/${event.id}`;

    // Preserve /edit mode and tab in URL
    const baseWithEdit = openInEditMode ? `${basePath}/edit` : basePath;
    const newPath = paramTab ? `${baseWithEdit}/${paramTab}` : baseWithEdit;

    if (currentPath !== newPath) {
      navigate(newPath, { replace: true });
    }
  }, [event?.id, isOpen, paramEventId, domain, paramTab, openInEditMode, navigate]);

  const handleSaveActivity = async (activityData) => {
    const response = await adminApi.createVCXEventActivity(
      event.id,
      activityData
    );

    // Add to Redux store
    dispatch(addActivity(response.data));

    // Refresh activities list from server to ensure consistency
    await fetchActivities();

    // Refetch the parent event to get updated event_metadata.follow_ups
    // The backend sync should have updated follow_ups array when activity was saved
    try {
      const eventResponse = await adminApi.getVCXEvent(event.id);

      if (eventResponse.data) {
        // Update Redux cache with fresh event data
        dispatch(updateEvent(eventResponse.data));
        dispatch(setSelectedEvent(eventResponse.data));

        // Also notify parent component to update its state
        if (onUpdate) {
          await onUpdate(eventResponse.data);
        }
      }
    } catch (error) {
      console.error('Failed to refresh event after activity save:', error);
      // Don't throw - the activity was saved successfully, just the refresh failed
    }

    return response.data;
  };

  const handleResolveFollowUp = async (followUp) => {
    try {
      // Create a new activity documenting the resolution
      const resolutionActivity = {
        activity_type: 'follow_up_updated',
        notes: `Resolved follow-up${followUp.notes ? ': ' + followUp.notes : ''}`,
        is_automated: false,
      };

      await handleSaveActivity(resolutionActivity);

      // Update the original activity to mark follow_up_required as false
      await adminApi.updateVCXEventActivity(event.id, followUp.activity_id, {
        follow_up_required: false
      });

      // Refresh activities to show updated state
      await fetchActivities();

      // Refresh event to update follow-ups list
      const eventResponse = await adminApi.getVCXEvent(event.id);
      if (eventResponse.data) {
        dispatch(updateEvent(eventResponse.data));
        dispatch(setSelectedEvent(eventResponse.data));
        if (onUpdate) {
          await onUpdate(eventResponse.data);
        }
      }

      // Show success message
      setAlertState({ show: true, type: 'success', title: '',
        message: 'The follow-up has been marked as resolved.'
      });
    } catch (error) {
      setAlertState({ show: true, type: 'error', title: '',
        message: error.response?.data?.message || 'Failed to resolve follow-up. Please try again.'
      });
    }
  };

  const hasData = (obj) => obj && Object.keys(obj).length > 0;

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);

    // If using URL params (standalone mode), navigate to path with tab
    if (paramEventId) {
      const basePath = domain
        ? `/admin/vcx-events/domain/${domain}/${paramEventId}`
        : `/admin/vcx-events/${paramEventId}`;

      // Preserve /edit mode in URL
      const baseWithEdit = openInEditMode ? `${basePath}/edit` : basePath;
      const newPath = tabId === 'summary' ? baseWithEdit : `${baseWithEdit}/${tabId}`;
      navigate(newPath, { replace: true });
    } else {
      // Otherwise use query params (legacy sidebar mode)
      const newParams = new URLSearchParams(searchParams);
      if (tabId === 'summary') {
        newParams.delete('tab');
      } else {
        newParams.set('tab', tabId);
      }
      setSearchParams(newParams, { replace: true });
    }
  };

  const handleClose = async () => {
    // Submit batched feedback before closing
    await submitBatchedFeedback();

    // Clear tab query param when closing
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('tab');
    setSearchParams(newParams, { replace: true });
    // Reset to summary tab
    setActiveTab('summary');
    // Call original onClose
    onClose();
  };

  const handleNavigate = async (direction) => {
    // Submit batched feedback before navigating to next/prev event
    await submitBatchedFeedback();

    if (isNavigating) return;

    setIsNavigating(true);
    // Close edit mode when navigating
    setIsEditing(false);
    setEditedData({});

    if (onNavigate) {
      onNavigate(direction);
    }

    // Re-enable after 400ms to let the remount settle
    clearTimeout(navigateTimerRef.current);
    navigateTimerRef.current = setTimeout(() => setIsNavigating(false), 400);
  };

  const handleViewCallFromActivity = (callId, tab) => {
    setSelectedCallId(callId);
    setViewerOpen(true);

    // Set the tab query param if specified
    if (tab) {
      const newParams = new URLSearchParams(searchParams);
      newParams.set('tab', tab);
      setSearchParams(newParams, { replace: true });
    }
  };

  // Show loading state when fetching event
  if (paramEventId && loadingEvent && !event) {
    return (
      <div className="container mt-5">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3 text-muted">Loading event details...</p>
        </div>
      </div>
    );
  }

  // Show error state when event failed to load
  if (paramEventId && eventError && !event) {
    return (
      <div className="container mt-5">
        <div className="alert alert-danger">
          <h5><i className="fas fa-exclamation-triangle me-2"></i>Error Loading Event</h5>
          <p className="mb-0">{eventError}</p>
          <button className="btn btn-sm btn-outline-danger mt-2" onClick={() => navigate(-1)}>
            <i className="fas fa-arrow-left me-1"></i>Go Back
          </button>
        </div>
      </div>
    );
  }

  if (!event) return null;

  const channelIcons = {
    phone: 'fa-phone',
    sms: 'fa-comment-sms',
    email: 'fa-envelope',
    ehr_message: 'fa-hospital',
    teams: 'fa-users',
    fax: 'fa-fax',
    in_person: 'fa-user',
    other: 'fa-circle-question',
  };

  const directionIcons = {
    inbound: 'fa-arrow-down',
    outbound: 'fa-arrow-up',
    internal: 'fa-exchange-alt',
  };

  const renderSummaryTab = () => {
    if (!event) return null;
    return (
      <div className="p-3">
        {/* Alert Messages */}
        {alertState.show && (
           <AlertMessage
            type={alertState.type}
            title={alertState.title}
            message={alertState.message}
            onDismiss={() => setAlertState({ ...alertState, show: false })}
            className="mb-3"
          />
        )}

        {/* Archived Badge */}
        {event.is_active === false && (
          <div className="alert alert-warning mb-3">
            <i className="fas fa-archive me-2"></i>
            <strong>Archived Event</strong> - This event is hidden from the active event list.
          </div>
        )}

        {/* Event Classification */}
        <div className="card mb-3">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center card-title text-primary mb-3">
              <div className="d-flex align-items-center gap-3">
                <span>Basic Information</span>
                {/* <InlineFeedback
                  type="thumbs"
                  size="sm"
                  currentValue={batchedFeedback.overall_thumbs ?? event.event_metadata?.feedback?.overall_thumbs}
                  onSubmit={(value) => handleInlineFeedbackSubmit('overall_thumbs', value)}
                  label="Quality"
                /> */}
              </div>
              <div className="d-flex align-items-center gap-2">

              {!showActivityModal && !isEditing && (
                <button
                  className="btn btn-sm btn-danger"
                  onClick={() => {
                    setShowActivityModal(true);
                    setShouldPulseActivity(true);
                  }}
                >
                  <i className="fa fa-plus me-1"></i>
                  Take Action
                </button>
              )}
                {!isEditing && (
                  <>
                    <button
                      className="btn btn-sm btn-primary ms-2"
                      onClick={handleEditClick}
                    >
                      <i className="fas fa-edit me-1"></i>
                      Edit
                    </button>
                    {isAnkrAdmin && (
                      <>
                        {event.is_active === false ? (
                          <button
                            className="btn btn-sm btn-outline-success ms-2"
                            onClick={handleRestoreEvent}
                            title="Restore this archived event"
                          >
                            <i className="fas fa-undo me-1"></i>
                            Restore
                          </button>
                        ) : (
                          <button
                            className="btn btn-sm btn-warning ms-2"
                            onClick={handleArchiveEvent}
                            title="Archive this event"
                          >
                            <i className="fas fa-archive me-1"></i>
                            Archive
                          </button>
                        )}
                        <button
                          className="btn btn-sm btn-outline-danger ms-2"
                          onClick={handlePermanentDelete}
                          title="Permanently delete this event (admin only - cannot be undone)"
                        >
                          <i className="fas fa-trash me-1"></i>
                          Delete
                        </button>
                      </>
                    )}
                  </>
                )}
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                {!isEditing ? (
                  <p className="mb-2">
                    <strong>Event ID: </strong>
                    <small className="text-muted font-monospace">{event.id.slice(0, 5)}...{event.id.slice(-5)}</small>
                    <button
                      className="btn btn-sm btn-link p-0 ms-2"
                      onClick={() => copyToClipboard(event.id, 'Event ID')}
                      title="Copy Event ID"
                    >
                      <i className={`fas ${copied === 'Event ID' ? 'fa-check text-success' : 'fa-copy'}`}></i>
                    </button>
                  </p>
                ) : (
                  <EditableField
                    label="Event ID"
                    field="id"
                    type="text"
                    value={event.id}
                    isEditing={isEditing}
                    editedData={editedData}
                    handleFieldChange={handleFieldChange}
                    disabled={true}
                  />
                )}
              </div>
              <div className="col-md-6">
                <EditableField
                  label="Created"
                  field="created_at"
                  type="text"
                  value={new Date(event.created_at).toLocaleString()}
                  isEditing={isEditing}
                  editedData={editedData}
                  handleFieldChange={handleFieldChange}
                  disabled={true}
                />
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <EditableField
                  label="Channel"
                  field="channel"
                  type="select"
                  value={event.channel}
                  isEditing={isEditing}
                  editedData={editedData}
                  handleFieldChange={handleFieldChange}
                  options={[
                    { value: 'phone', label: 'Phone' },
                    { value: 'sms', label: 'SMS' },
                    { value: 'email', label: 'Email' },
                    { value: 'ehr_message', label: 'EHR Message' },
                    { value: 'teams', label: 'Teams' },
                    { value: 'fax', label: 'Fax' },
                    { value: 'in_person', label: 'In Person' },
                    { value: 'other', label: 'Other' }
                  ]}
                />
              </div>
              <div className="col-md-6">
                <div className="mb-2">
                  <label className="form-label fw-bold mb-1">Event Outcome</label>
                  <div className="btn-group w-100" role="group">
                    <button
                      type="button"
                      className={`btn btn-sm ${(isEditing ? editedData.outcome ?? event.outcome : event.outcome) === 'new' ? 'btn-warning' : 'btn-outline-secondary'}`}
                      onClick={() => {
                        if (isEditing) {
                          setEditedData(prev => ({ ...prev, outcome: 'new' }));
                        } else {
                          handleQuickUpdate({ outcome: 'new' });
                        }
                      }}
                      disabled={saving}
                    ><i className="fas fa-exclamation-circle me-2"></i>New
                    </button>
                    <button
                      type="button"
                      className={`btn btn-sm ${(isEditing ? editedData.outcome ?? event.outcome : event.outcome) === 'in_progress' ? 'btn-info' : 'btn-outline-secondary'}`}
                      onClick={() => {
                        if (isEditing) {
                          setEditedData(prev => ({ ...prev, outcome: 'in_progress' }));
                        } else {
                          handleQuickUpdate({ outcome: 'in_progress' });
                        }
                      }}
                      disabled={saving}
                    ><i className="fas fa-clock me-2"></i>In Progress
                    </button>
                    <button
                      type="button"
                      className={`btn btn-sm ${(isEditing ? editedData.outcome ?? event.outcome : event.outcome) === 'resolved' ? 'btn-success' : 'btn-outline-secondary'}`}
                      onClick={() => {
                        if (isEditing) {
                          setEditedData(prev => ({ ...prev, outcome: 'resolved' }));
                        } else {
                          handleQuickUpdate({ outcome: 'resolved' });
                        }
                      }}
                      disabled={saving}
                    ><i className="fas fa-check-circle me-2"></i>Resolved
                    </button>
                  </div>
                  {isAnkrAdmin && event.event_metadata?.resolution_type === 'auto_closed' && (
                    <small className="text-muted d-block mt-1">
                      <i className="fas fa-robot me-1"></i>
                      Auto-closed: {event.event_metadata?.resolution_comment || 'Quick disconnect'}
                    </small>
                  )}
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <div className="mb-2">
                  <label className="form-label fw-bold mb-1">Priority</label>
                  <div className="btn-group w-100" role="group">
                    <button
                      type="button"
                      className={`btn btn-sm ${(isEditing ? editedData.event_metadata?.priority ?? event.event_metadata?.priority : event.event_metadata?.priority) === 'low' ? 'btn-secondary' : 'btn-outline-secondary'}`}
                      onClick={() => {
                        if (isEditing) {
                          setEditedData(prev => ({
                            ...prev,
                            event_metadata: { ...(prev.event_metadata || {}), priority: 'low' }
                          }));
                        } else {
                          handleQuickUpdate({ event_metadata: { priority: 'low' } });
                        }
                      }}
                      disabled={saving}
                    >
                      Low
                    </button>
                    <button
                      type="button"
                      className={`btn btn-sm ${(isEditing ? editedData.event_metadata?.priority ?? event.event_metadata?.priority : event.event_metadata?.priority) === 'medium' ? 'btn-info' : 'btn-outline-secondary'}`}
                      onClick={() => {
                        if (isEditing) {
                          setEditedData(prev => ({
                            ...prev,
                            event_metadata: { ...(prev.event_metadata || {}), priority: 'medium' }
                          }));
                        } else {
                          handleQuickUpdate({ event_metadata: { priority: 'medium' } });
                        }
                      }}
                      disabled={saving}
                    >
                      Medium
                    </button>
                    <button
                      type="button"
                      className={`btn btn-sm ${(isEditing ? editedData.event_metadata?.priority ?? event.event_metadata?.priority : event.event_metadata?.priority) === 'high' ? 'btn-warning' : 'btn-outline-secondary'}`}
                      onClick={() => {
                        if (isEditing) {
                          setEditedData(prev => ({
                            ...prev,
                            event_metadata: { ...(prev.event_metadata || {}), priority: 'high' }
                          }));
                        } else {
                          handleQuickUpdate({ event_metadata: { priority: 'high' } });
                        }
                      }}
                      disabled={saving}
                    >
                      High
                    </button>
                    <button
                      type="button"
                      className={`btn btn-sm ${(isEditing ? editedData.event_metadata?.priority ?? event.event_metadata?.priority : event.event_metadata?.priority) === 'urgent' ? 'btn-danger' : 'btn-outline-secondary'}`}
                      onClick={() => {
                        if (isEditing) {
                          setEditedData(prev => ({
                            ...prev,
                            event_metadata: { ...(prev.event_metadata || {}), priority: 'urgent' }
                          }));
                        } else {
                          handleQuickUpdate({ event_metadata: { priority: 'urgent' } });
                        }
                      }}
                      disabled={saving}
                    >
                      🔴 Urgent
                    </button>
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                {!isEditing ? (
                  <div className="mb-2">
                    <label className="form-label fw-bold mb-1">Assigned To</label>
                    <div className="d-flex align-items-center">
                      <span className="flex-grow-1">{event.event_metadata?.assigned_to_name || 'Unassigned'}</span>
                    
                      {(() => {
                        const djangoUserId = currentUser?.djangoUserId;

                        // Hide button for virtual users (admin/general tokens with no Django user ID)
                        if (!djangoUserId) {
                          return null;
                        }

                        // Hide button if already assigned to me
                        const isAssignedToMe = String(event.event_metadata?.assigned_to) === String(djangoUserId);
                        if (isAssignedToMe) {
                          return null;
                        }

                        return (
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => {
                              const userName = currentUser?.firstName && currentUser?.lastName
                                ? `${currentUser.firstName} ${currentUser.lastName}`
                                : currentUser?.email || currentUser?.username || 'Unknown';
                              if (isEditing) {
                                setEditedData(prev => ({
                                  ...prev,
                                  event_metadata: {
                                    ...(prev.event_metadata || {}),
                                    assigned_to: djangoUserId,
                                    assigned_to_name: userName
                                  }
                                }));
                              } else {
                                handleQuickUpdate({
                                  event_metadata: {
                                    assigned_to: djangoUserId,
                                    assigned_to_name: userName
                                  }
                                });
                              }
                            }}
                            disabled={saving}
                          >
                            <i className="fa fa-user me-1"></i>
                            Assign Me
                          </button>
                        );
                      })()}
                    </div>
                  </div>
                ) : (
                  <div className="mb-2">
                    <label className="form-label fw-bold mb-1">Assigned To</label>
                    <div className="d-flex gap-2">
                      <select
                        className="form-select form-select-sm flex-grow-1"
                        value={editedData.event_metadata?.assigned_to ?? (event.event_metadata?.assigned_to || '')}
                        onChange={(e) => {
                          const userId = e.target.value;
                          const selectedUser = users.find(u => String(u.id) === String(userId));

                          // Update both assigned_to (ID) and assigned_to_name
                          setEditedData(prev => ({
                            ...prev,
                            event_metadata: {
                              ...(prev.event_metadata || {}),
                              assigned_to: userId || null,
                              assigned_to_name: selectedUser ? getUserDisplayName(selectedUser) : null
                            }
                          }));
                        }}
                        disabled={loadingUsers}
                      >
                      <option value="">Unassigned</option>
                      {users.map((user, index) => {
                        const showHeader = index === 0 || users[index - 1].user_type !== user.user_type;
                        const displayName = user.first_name && user.last_name
                          ? `${user.last_name}, ${user.first_name}`
                          : user.email || user.username;

                        return (
                          <React.Fragment key={user.id}>
                            {showHeader && (
                              <option disabled style={{ fontWeight: 'bold', backgroundColor: '#f0f0f0' }}>
                                {user.user_type === 'ankr' ? '── Ankr Staff ──' : '── Healthcare Staff ──'}
                              </option>
                            )}
                            <option value={user.id}>{displayName}</option>
                          </React.Fragment>
                        );
                      })}
                      </select>
                      {loadingUsers && <small className="text-muted">Loading users...</small>}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Follow-up Status - Display all follow-ups from event_metadata.follow_ups */}
            {(() => {
              const followUps = event.event_metadata?.follow_ups || [];
              const hasFollowUps = followUps.length > 0;

              return (
                <>
                  <div className="row">
                    <div className="col-md-12">
                      <p className="mb-2">
                        <strong>Follow-up Required:</strong>{' '}
                        <span className={`badge bg-${hasFollowUps ? 'warning' : 'secondary'}`}>
                          {hasFollowUps ? (
                            <>
                              <i className="fa fa-bell me-1"></i>
                              Yes ({followUps.length} {followUps.length === 1 ? 'activity' : 'activities'})
                            </>
                          ) : (
                            'No'
                          )}
                        </span>
                      </p>
                    </div>
                  </div>

                  {hasFollowUps && (
                    <div className="row">
                      <div className="col-md-12">
                        <div className="card bg-light border-warning">
                          <div className="card-body p-2">
                            {/* <h6 className="card-title text-primary mb-2">
                              <i className="fa fa-tasks me-2"></i>
                              Follow-ups
                            </h6> */}
                            {followUps.map((followUp, index) => (
                              <div key={followUp.activity_id} className={`${index > 0 ? 'border-top pt-2 mt-2' : ''}`}>
                                <div className="d-flex justify-content-between align-items-start">
                                  <div className="flex-grow-1">
                                    <div className="mb-1">
                                      <strong className="text-primary">{followUp.action || ''}</strong>
                                      {followUp.assigned_to_name && (
                                        <>
                                        Created by
                                        <span className="badge bg-info ms-2">
                                          <i className="fa fa-user me-1"></i>
                                          {followUp.assigned_to_name}
                                        </span>
                                        </>
                                      )}
                                    </div>
                                    {followUp.follow_up_date && (
                                      <div className="text-muted small">
                                        <i className="fa fa-calendar me-1"></i>
                                        <strong>Due:</strong> {new Date(followUp.follow_up_date).toLocaleString()}
                                      </div>
                                    )}
                                    {followUp.notes && (
                                      <div className="text-muted small mt-1">
                                        <i className="fa fa-sticky-note me-1"></i>
                                        {followUp.notes}
                                      </div>
                                    )}
                                    <div className="text-muted small">
                                      <i className="fa fa-clock me-1"></i>
                                      Created: {new Date(followUp.created_at).toLocaleString()}
                                    </div>
                                  </div>
                                  <div className="d-flex gap-2">
                                    <button
                                      className="btn btn-sm btn-success"
                                      onClick={() => handleResolveFollowUp(followUp)}
                                      title="Mark this follow-up as resolved"
                                    >
                                      <i className="fa fa-check me-1"></i>
                                      Done
                                    </button>
                                    <button
                                      className="btn btn-sm btn-outline-primary"
                                      onClick={() => {
                                        // Switch to activities tab and scroll to this activity
                                        handleTabChange('activities');
                                        // Wait for tab to render then scroll
                                        setTimeout(() => {
                                          const activityElement = document.getElementById(`activity-${followUp.activity_id}`);
                                          if (activityElement) {
                                            activityElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                            // Highlight briefly
                                            activityElement.classList.add('bg-warning', 'bg-opacity-25');
                                            setTimeout(() => {
                                              activityElement.classList.remove('bg-warning', 'bg-opacity-25');
                                            }, 2000);
                                          }
                                        }, 100);
                                      }}
                                      title="Jump to activity"
                                    >
                                      <i className="fa fa-arrow-right me-1"></i>
                                      View
                                    </button>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              );
            })()}

            <div className="row">
              <div className="col-md-6">
                <EditableField
                  label="Direction"
                  field="direction"
                  type="select"
                  value={event.direction}
                  isEditing={isEditing}
                  editedData={editedData}
                  handleFieldChange={handleFieldChange}
                  options={[
                    { value: 'inbound', label: 'Inbound' },
                    { value: 'outbound', label: 'Outbound' },
                    { value: 'internal', label: 'Internal' }
                  ]}
                />
              </div>
              <div className="col-md-6">
                <EditableField
                  label="Call Domain"
                  field="call_domain"
                  type="select"
                  value={event.call_domain}
                  isEditing={isEditing}
                  editedData={editedData}
                  handleFieldChange={handleFieldChange}
                  options={CALL_DOMAIN_OPTIONS}
                />
              </div>
              {isAnkrAdmin && (
                <>
                <div className="col-md-6">
                  <EditableField
                    label="Caller Intent"
                    field="caller_intent"
                    description="Admin only"
                    type="text"
                    value={event.caller_intent}
                    isEditing={isEditing}
                    editedData={editedData}
                    handleFieldChange={handleFieldChange}
                  />
                </div>
                <div className="col-md-6">
              <EditableField
                label="Action"
                field="action"
                type="text"
                description="Admin only"
                value={event.action}
                isEditing={isEditing}
                editedData={editedData}
                handleFieldChange={handleFieldChange}
              /></div>
            </>
              )}
            </div>


            {/* {event.follow_up_required && event.follow_up_date && (
              <div className="row">
                <div className="col-md-6">
                  <EditableField
                    label="Follow-up Date"
                    field="follow_up_date"
                    type="text"
                    value={new Date(event.follow_up_date).toLocaleString()}
                    isEditing={isEditing}
                    editedData={editedData}
                    handleFieldChange={handleFieldChange}
                    disabled={true}
                  />
                </div>
              </div> 
            )}*/}

            {/* Caller Information */}
            <hr className="my-3" />
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="text-muted mb-0 me-2">Caller
              {(event.event_metadata?.caller_data?.caller_first_name || event.event_metadata?.caller_data?.caller_last_name || event.event_metadata?.caller_data?.caller_name) && (
                <span className="badge bg-success ms-2" style={{fontSize: '0.65rem', padding: '2px 4px'}}>
                  ✓
                </span>
              )}
              {!(event.event_metadata?.caller_data?.caller_first_name || event.event_metadata?.caller_data?.caller_last_name || event.event_metadata?.caller_data?.caller_name) &&
                (event.extracted_patient_data?.caller_first_name || event.extracted_patient_data?.caller_last_name || event.extracted_patient_data?.caller_name) && (
                <span className="badge bg-info ms-2" style={{fontSize: '0.65rem', padding: '2px 4px'}}>
                  AI
                </span>
              )}</h6>
              <div>
                <i className={`fas ${directionIcons[event.direction] || 'fa-arrow-right'} me-2`}></i>
                <span className="badge bg-primary text-capitalize">{event.direction}</span>
                {event.is_automated && <span className="badge bg-info ms-1">Automated</span>}
              </div>
            </div>

            {/* Do we have caller info available? */}
            {(() => {
              const callerInfoAvailable = event.customer_name ||
                event.event_metadata?.caller_data?.caller_name ||
                event.event_metadata?.caller_data?.caller_first_name ||
                event.event_metadata?.caller_data?.caller_last_name ||
                event.extracted_patient_data?.caller_name ||
                event.extracted_patient_data?.caller_first_name ||
                event.extracted_patient_data?.caller_last_name || false;

              if (!isEditing && !callerInfoAvailable) return null;

              return (
                <div className="row">
                  <div className="col-md-6">
                    {!isEditing && callerInfoAvailable ? (
                      <>
                        <EditableField
                          label="Name"
                          field="customer_name"
                          type="text"
                          value={
                            event.customer_name ||
                            (event.event_metadata?.caller_data?.caller_first_name || event.event_metadata?.caller_data?.caller_last_name
                              ? [event.event_metadata.caller_data.caller_first_name, event.event_metadata.caller_data.caller_last_name].filter(Boolean).join(' ')
                              : event.event_metadata?.caller_data?.caller_name) ||
                            (event.extracted_patient_data?.caller_first_name || event.extracted_patient_data?.caller_last_name
                              ? [event.extracted_patient_data.caller_first_name, event.extracted_patient_data.caller_last_name].filter(Boolean).join(' ')
                              : event.extracted_patient_data?.caller_name) ||
                            ''
                          }
                          isEditing={isEditing}
                          editedData={editedData}
                          handleFieldChange={handleFieldChange}
                        />
                        {event.extracted_patient_data?.caller_details && !event.customer_name && !event.event_metadata?.caller_data?.caller_first_name && !event.event_metadata?.caller_data?.caller_last_name && (
                          <small className="text-muted d-block my-1">
                            <i className="fas fa-info-circle me-1"></i>
                            {event.extracted_patient_data.caller_details}
                          </small>
                        )}
                      </>
                    ) : isEditing ? (
                      <div className="mb-2">
                        <label className="form-label fw-bold mb-1">
                          First Name
                          {event.extracted_patient_data?.caller_name && (
                            <span className="badge bg-info ms-2" style={{fontSize: '0.65rem'}}>AI</span>
                          )}
                        </label>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          value={
                            editedData.event_metadata?.caller_data?.caller_first_name ||
                            event.event_metadata?.caller_data?.caller_first_name ||
                            event.extracted_patient_data?.caller_first_name ||
                            event.extracted_patient_data?.caller_name ||
                            ''
                          }
                          onChange={(e) => {
                            setEditedData(prev => ({
                              ...prev,
                              event_metadata: {
                                ...(prev.event_metadata || {}),
                                caller_data: {
                                  ...(prev.event_metadata?.caller_data || {}),
                                  caller_first_name: e.target.value
                                }
                              }
                            }));
                          }}
                          placeholder="Caller's first name"
                          style={{ backgroundColor: 'white' }}
                        />
                        {event.extracted_patient_data?.caller_details && (
                          <small className="text-muted d-block my-1">
                            <i className="fas fa-info-circle me-1"></i>
                            {event.extracted_patient_data.caller_details}
                          </small>
                        )}
                      </div>
                    ) : null}
                  </div>
                  <div className="col-md-6">
                    {!isEditing && callerInfoAvailable ? (
                      <EditableField
                        label="Customer Type"
                        field="customer_type"
                        type="select"
                        value={event.customer_type}
                        isEditing={isEditing}
                        editedData={editedData}
                        handleFieldChange={handleFieldChange}
                        options={[
                          { value: '', label: 'None' },
                          { value: 'patient', label: 'Patient' },
                          { value: 'family', label: 'Family' },
                          { value: 'vendor', label: 'Vendor' },
                          { value: 'insurance', label: 'Insurance' },
                          { value: 'other_provider', label: 'Other Provider' }
                        ]}
                      />
                    ) : isEditing ? (
                      <div className="mb-2">
                        <label className="form-label fw-bold mb-1">Last Name</label>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          value={
                            editedData.event_metadata?.caller_data?.caller_last_name ||
                            event.event_metadata?.caller_data?.caller_last_name ||
                            event.extracted_patient_data?.caller_last_name ||
                            ''
                          }
                          onChange={(e) => {
                            setEditedData(prev => ({
                              ...prev,
                              event_metadata: {
                                ...(prev.event_metadata || {}),
                                caller_data: {
                                  ...(prev.event_metadata?.caller_data || {}),
                                  caller_last_name: e.target.value
                                }
                              }
                            }));
                          }}
                          placeholder="Caller's last name"
                          style={{ backgroundColor: 'white' }}
                        />
                      </div>
                    ) : null}
                  </div>
                </div>
              );
            })()}

            {/* Customer Type in Edit Mode */}
            {isEditing && (
              <div className="row">
                <div className="col-md-6">
                  <EditableField
                    label="Customer Type"
                    field="customer_type"
                    type="select"
                    value={event.customer_type}
                    isEditing={isEditing}
                    editedData={editedData}
                    handleFieldChange={handleFieldChange}
                    options={[
                      { value: '', label: 'None' },
                      { value: 'patient', label: 'Patient' },
                      { value: 'family', label: 'Family' },
                      { value: 'vendor', label: 'Vendor' },
                      { value: 'insurance', label: 'Insurance' },
                      { value: 'other_provider', label: 'Other Provider' }
                    ]}
                  />
                </div>
              </div>
            )}

            <div className="row">
              <div className="col-md-6">
                <EditableField
                  label="Phone"
                  field="customer_phone"
                  type="tel"
                  value={event.customer_phone}
                  isEditing={isEditing}
                  editedData={editedData}
                  handleFieldChange={handleFieldChange}
                />
              </div>
              <div className="col-md-6">
                <EditableField
                  label="Email"
                  field="customer_email"
                  type="email"
                  value={event.customer_email}
                  isEditing={isEditing}
                  editedData={editedData}
                  handleFieldChange={handleFieldChange}
                />
              </div>
            </div>

            {/* Patient Information */}
            {(event.extracted_patient_data || event.event_metadata?.patient_data || isEditing) && (
              <>
                <hr className="my-3 opacity-50" />
                <div className="mb-3">
                  <h6 className="text-muted mb-2">
                    Patient
                    {(event.event_metadata?.patient_data?.patient_first_name || event.event_metadata?.patient_data?.patient_last_name) && (
                      <span className="badge bg-success ms-2" style={{fontSize: '0.65rem', padding: '2px 4px'}}>
                        ✓
                      </span>
                    )}
                    {!(event.event_metadata?.patient_data?.patient_first_name || event.event_metadata?.patient_data?.patient_last_name) &&
                      (event.extracted_patient_data?.patient_first_name || event.extracted_patient_data?.patient_last_name) && (
                      <span className="badge bg-info ms-2" style={{fontSize: '0.65rem', padding: '2px 4px'}}>
                        AI
                      </span>
                    )}
                  </h6>
                  <div className="row">
                    {/* Patient First Name */}
                    {(event.event_metadata?.patient_data?.patient_first_name || event.event_metadata?.patient_data?.patient_last_name || event.extracted_patient_data?.patient_first_name || event.extracted_patient_data?.patient_last_name || isEditing) && (
                      <div className="col-md-6 mb-2">
                        {!isEditing ? (
                          <div className="d-flex justify-content-between align-items-center">
                            <p className="mb-0">
                              <strong>Patient Name:</strong>{' '}
                              <span className="text-primary">
                                {[
                                  event.event_metadata?.patient_data?.patient_first_name || event.extracted_patient_data?.patient_first_name,
                                  event.event_metadata?.patient_data?.patient_last_name || event.extracted_patient_data?.patient_last_name
                                ].filter(Boolean).join(' ')}
                              </span>
                            </p>
                            <FieldValidator
                              fieldName="patient_name"
                              currentStatus={
                                batchedFeedback.patient_data_validation?.patient_name ??
                                event.event_metadata?.feedback?.patient_data_validation?.patient_name
                              }
                              onValidate={handleFieldValidation}
                              size="sm"
                            />
                          </div>
                        ) : (
                          <div className="mb-2">
                            <label className="form-label fw-bold mb-1">First Name</label>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              value={editedData.event_metadata?.patient_data?.patient_first_name ?? event.event_metadata?.patient_data?.patient_first_name ?? event.extracted_patient_data?.patient_first_name ?? ''}
                              onChange={(e) => {
                                setEditedData(prev => ({
                                  ...prev,
                                  event_metadata: {
                                    ...(prev.event_metadata || {}),
                                    patient_data: {
                                      ...(prev.event_metadata?.patient_data || {}),
                                      patient_first_name: e.target.value
                                    }
                                  }
                                }));
                              }}
                              style={{ backgroundColor: 'white' }}
                            />
                          </div>
                        )}
                      </div>
                    )}

                    {/* Patient Last Name */}
                    {(event.extracted_patient_data?.patient_first_name || event.extracted_patient_data?.patient_last_name || isEditing) && (
                      <div className="col-md-6 mb-2">
                        {isEditing && (
                          <div className="mb-2">
                            <label className="form-label fw-bold mb-1">Last Name</label>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              value={editedData.event_metadata?.patient_data?.patient_last_name ?? event.event_metadata?.patient_data?.patient_last_name ?? event.extracted_patient_data?.patient_last_name ?? ''}
                              onChange={(e) => {
                                setEditedData(prev => ({
                                  ...prev,
                                  event_metadata: {
                                    ...(prev.event_metadata || {}),
                                    patient_data: {
                                      ...(prev.event_metadata?.patient_data || {}),
                                      patient_last_name: e.target.value
                                    }
                                  }
                                }));
                              }}
                              style={{ backgroundColor: 'white' }}
                            />
                          </div>
                        )}
                      </div>
                    )}

                    {/* Patient DOB */}
                    {(event.event_metadata?.patient_data?.patient_dob || event.extracted_patient_data?.patient_dob || isEditing) && (
                      <div className="col-md-6 mb-2">
                        {!isEditing ? (
                          <div className="d-flex justify-content-between align-items-center">
                            <p className="mb-0">
                              <strong>Date of Birth:</strong>{' '}
                              <span className="text-primary">
                                {event.event_metadata?.patient_data?.patient_dob || event.extracted_patient_data?.patient_dob}
                              </span>
                            </p>
                            <FieldValidator
                              fieldName="patient_dob"
                              currentStatus={
                                batchedFeedback.patient_data_validation?.patient_dob ??
                                event.event_metadata?.feedback?.patient_data_validation?.patient_dob
                              }
                              onValidate={handleFieldValidation}
                              size="sm"
                            />
                          </div>
                        ) : (
                          <div className="mb-2">
                            <label className="form-label fw-bold mb-1">Date of Birth</label>
                            <input
                              type="date"
                              className="form-control form-control-sm"
                              placeholder="MM/DD/YYYY"
                              value={editedData.event_metadata?.patient_data?.patient_dob ?? event.event_metadata?.patient_data?.patient_dob ?? event.extracted_patient_data?.patient_dob ?? ''}
                              onChange={(e) => {
                                setEditedData(prev => ({
                                  ...prev,
                                  event_metadata: {
                                    ...(prev.event_metadata || {}),
                                    patient_data: {
                                      ...(prev.event_metadata?.patient_data || {}),
                                      patient_dob: e.target.value
                                    }
                                  }
                                }));
                              }}
                              style={{ backgroundColor: 'white' }}
                            />
                          </div>
                        )}
                      </div>
                    )}

                    {/* EHR Patient ID */}
                    {(event.event_metadata?.patient_data?.match_patient_id || event.extracted_patient_data?.match_patient_id || isEditing) && (
                      <div className="col-md-6 mb-2">
                        {!isEditing ? (
                          <p className="mb-0">
                            <strong>EHR Patient ID:</strong>{' '}
                            <span className="text-muted">
                              {event.event_metadata?.patient_data?.match_patient_id || event.extracted_patient_data?.match_patient_id}
                            </span>
                          </p>
                        ) : (
                          <div className="mb-2">
                            <label className="form-label fw-bold mb-1">EHR Patient ID</label>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              value={editedData.event_metadata?.patient_data?.match_patient_id ?? event.event_metadata?.patient_data?.match_patient_id ?? event.extracted_patient_data?.match_patient_id ?? ''}
                              onChange={(e) => {
                                setEditedData(prev => ({
                                  ...prev,
                                  event_metadata: {
                                    ...(prev.event_metadata || {}),
                                    patient_data: {
                                      ...(prev.event_metadata?.patient_data || {}),
                                      match_patient_id: e.target.value
                                    }
                                  }
                                }));
                              }}
                              style={{ backgroundColor: 'white' }}
                            />
                          </div>
                        )}
                      </div>
                    )}

                    {/* Patient Type Indicator - Three-state system */}
                    {event.extracted_patient_data?.patient_type ? (
                      <div className="col-md-6 mb-2">
                        <p className="mb-0">
                          <span className={`badge ${
                            event.extracted_patient_data.patient_type === 'existing' ? 'bg-success' :
                            event.extracted_patient_data.patient_type === 'new' ? 'bg-primary' :
                            'bg-warning'
                          }`}>
                            {event.extracted_patient_data.patient_type === 'existing' ? 'Existing Patient' :
                             event.extracted_patient_data.patient_type === 'new' ? 'New Patient' :
                             'Unverified Patient'}
                          </span>
                        </p>
                      </div>
                    ) : event.extracted_patient_data?.existing_patient && (
                      <div className="col-md-6 mb-2">
                        <p className="mb-0">
                          <span className={`badge ${event.extracted_patient_data.existing_patient === 'true' ? 'bg-success' : 'bg-warning'}`}>
                            {event.extracted_patient_data.existing_patient === 'true' ? 'Existing Patient' : 'New Patient'}
                          </span>
                          <small className="text-muted ms-2">(Legacy)</small>
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}

            {/* Related Entities */}
            <hr className="my-3" />
            <h6 className="text-muted mb-3">Related Entities</h6>

            {/* Health System - Editable dropdown for admins only */}
            {isAnkrAdmin && (
              <div className="row">
                <div className="col-md-6">
                  {!isEditing ? (
                    event.health_system_name && (
                      <p className="mb-2">
                        <strong>Health System:</strong> {event.health_system_name}
                      </p>
                    )
                  ) : (
                    <div className="mb-2">
                      <label className="form-label fw-bold mb-1">
                        Health System
                        {loadingHealthSystems && (
                          <span className="spinner-border spinner-border-sm ms-2" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </span>
                        )}
                      </label>
                      <select
                        className="form-select form-select-sm"
                        value={editedData.health_system || ''}
                        onChange={(e) => handleFieldChange('health_system', e.target.value)}
                        disabled={loadingHealthSystems}
                      >
                        <option value="">Select a health system</option>
                        {healthSystems.map(hs => (
                          <option key={hs.id} value={hs.id}>{hs.name}</option>
                        ))}
                      </select>
                      <small className="text-muted">Change which health system this event belongs to</small>
                    </div>
                  )}
                </div>
                {/* VoiceAI Provider Event ID - Editable by admins */}
                <div className="col-md-6 d-flex d-flex-inline">
                  <EditableField
                    label="Provider Event ID"
                    field="voiceai_provider_event_id"
                    type="text"
                    value={event.voiceai_provider_event_id || ''}
                    isEditing={isEditing}
                    editedData={editedData}
                    handleFieldChange={handleFieldChange}
                    disabled={!isAnkrAdmin}
                    description="Unique event ID from voice AI provider (call_id, chat_id, etc.)"
                  />

                  <a href={`https://dashboard.retellai.com/call-history?call_id=${event.voiceai_provider_event_id || ''}&history=${event.voiceai_provider_event_id || ''}`} target="_blank" rel="noreferrer">
                    <i className="fas fa-external-link text-primary"></i>
                  </a>
                  </div>
              </div>
            )}
            {!isAnkrAdmin && event.health_system_name && (
              <div className="row">
                <div className="col-md-6">
                  <EditableField
                    label="Health System"
                    field="health_system_name"
                    type="text"
                    value={event.health_system_name}
                    isEditing={isEditing}
                    editedData={editedData}
                    handleFieldChange={handleFieldChange}
                    disabled={true}
                  />
                </div>
              </div>
            )}

            {/* Patient - Read-only */}
            {event.patient && (
              <div className="row">
                <div className="col-md-6">
                  <EditableField
                    label="Patient ID"
                    field="patient"
                    type="text"
                    value={event.patient}
                    isEditing={isEditing}
                    editedData={editedData}
                    handleFieldChange={handleFieldChange}
                    disabled={true}
                  />
                </div>
                {event.patient_name && (
                  <div className="col-md-6">
                    <EditableField
                      label="Patient Name"
                      field="patient_name"
                      type="text"
                      value={event.patient_name}
                      isEditing={isEditing}
                      editedData={editedData}
                      handleFieldChange={handleFieldChange}
                      disabled={true}
                    />
                  </div>
                )}
              </div>
            )}

            {/* Provider - Read-only */}
            {isAnkrAdmin && event.provider && (
              <div className="row">
                <div className="col-md-6">
                  <EditableField
                    label="Provider ID"
                    field="provider"
                    type="text"
                    value={event.provider}
                    isEditing={isEditing}
                    editedData={editedData}
                    handleFieldChange={handleFieldChange}
                    disabled={true}
                  />
                </div>
              </div>
            )}

            {/* Case - Read-only */}
            {event.mirasolcase && (
              <div className="row">
                <div className="col-md-6">
                  <EditableField
                    label="Case ID"
                    field="mirasolcase"
                    type="text"
                    value={event.mirasolcase}
                    isEditing={isEditing}
                    editedData={editedData}
                    handleFieldChange={handleFieldChange}
                    disabled={true}
                  />
                </div>
              </div>
            )}

            {/* VoiceAI Agent - Read-only */}
            {isAnkrAdmin && event.voiceai_agent && (
              <div className="row">
                <div className="col-md-6">
                  <EditableField
                    label="Agent ID"
                    field="voiceai_agent"
                    type="text"
                    value={event.voiceai_agent}
                    isEditing={isEditing}
                    editedData={editedData}
                    handleFieldChange={handleFieldChange}
                    disabled={true}
                  />
                </div>
                {event.voiceai_agent_name && (
                  <div className="col-md-6">
                    <EditableField
                      label="Agent Name"
                      field="voiceai_agent_name"
                      type="text"
                      value={event.voiceai_agent_name}
                      isEditing={isEditing}
                      editedData={editedData}
                      handleFieldChange={handleFieldChange}
                      disabled={true}
                    />
                  </div>
                )}
              </div>
            )}

            {/* Performed By - Read-only */}
            {event.performed_by && (
              <div className="row">
                <div className="col-md-6">
                  <EditableField
                    label="Performed By (User ID)"
                    field="performed_by"
                    type="text"
                    value={event.performed_by}
                    isEditing={isEditing}
                    editedData={editedData}
                    handleFieldChange={handleFieldChange}
                    disabled={true}
                  />
                </div>
                {event.performed_by_name && (
                  <div className="col-md-6">
                    <EditableField
                      label="Performed By Name"
                      field="performed_by_name"
                      type="text"
                      value={event.performed_by_name}
                      isEditing={isEditing}
                      editedData={editedData}
                      handleFieldChange={handleFieldChange}
                      disabled={true}
                    />
                  </div>
                )}
              </div>
            )}

            {/* Event Timestamps */}
            {hasData(event.event_timestamps) && (
              <>
                <hr className="my-3" />
                <h6 className="text-muted mb-3">Event Timestamps</h6>
                {Object.entries(event.event_timestamps).map(([key, value]) => (
                  <p key={key} className="mb-1">
                    <strong>{key}:</strong> {value}
                  </p>
                ))}
              </>
            )}
            {/* Save/Cancel Buttons */}
        {isEditing && (
          <div className="d-flex gap-2 my-3">
            <button type="button" className="btn btn-primary"
              onClick={handleSaveEdit}
              disabled={saving || Object.keys(changedData).length === 0}
            >{saving ? (
                <>
                  <span className="spinner-border spinner-border-sm me-1"></span>
                  Saving...
                </>
              ) : (
                <>Save Changes</>
              )}
            </button>
            <button type="button"  className="btn btn-secondary"
              onClick={handleCancelEdit} disabled={saving}
            >Cancel
            </button>
          </div>
        )}
          </div>
        </div>

        {/* Event Notes */}
        {!isEditing && hasData(event.event_notes) && (
          <div className="card mb-3">
            <div className="card-body">
              <div className="card-title d-flex justify-content-between align-items-center mb-3">
                <div className="d-flex align-items-center gap-3">
                  <span className="card-title text-primary border-0 mb-0"> Event Summary </span>
                </div>
                {(event.voiceai_provider_event_id || event.voiceai_data?.call_id) && (
                  <div>
                    <button
                    className="btn btn-sm btn-primary me-3"
                    onClick={() => handleTabChange('transcript')}
                  >
                    <i className="fas fa-file-alt me-1"></i>
                    View Transcript
                  </button>

                  <InlineFeedback
                    type="stars"
                    size="sm"
                    currentValue={batchedFeedback.overall_stars ?? event.event_metadata?.feedback?.overall_stars}
                    onSubmit={(value) => handleInlineFeedbackSubmit('overall_stars', value)}
                    label="Your rating"
                  />
                  </div>
                )}
              </div>
              {/* {Object.entries(event.event_notes).map(([key, value]) => {
                // Skip transcript and summary in the card view - show them in the viewer
                if (key === 'transcript' || key === 'summary') {
                  return null;
                }
                return (
                  <p key={key} className="mb-1">
                    <strong className="text-capitalize">{key.replace(/_/g, ' ')}:</strong>{' '}
                    {typeof value === 'string' ? value : JSON.stringify(value)}
                  </p>
                );
              })} */}
              <div className="mt-2 d-flex justify-content-between align-items-start">
                {/* <strong>Summary:</strong> */}
                <p className="mb-0 mt-1 text-muted flex-grow-1">{event.event_notes.summary?.trim() || 'Not available'}</p>
                {event.event_notes.summary?.trim() && (
                  <button
                    className="btn btn-sm btn-outline-primary"
                    onClick={() => copyToClipboard(event.event_notes.summary?.trim(), 'Summary')}
                    title="Copy Summary"
                  >
                    <i className={`fas ${copied === 'Summary' ? 'fa-check' : 'fa-copy'} me-1`}></i>
                    {copied === 'Summary' ? 'Copied!' : 'Copy'}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Timestamps */}
        {/* <div className="card mb-3">
          <div className="card-body">
            <h6 className="card-title text-primary mb-3">
              Timestamps
            </h6>
            <div className="row">
              <div className="col-md-6">
                <p className="mb-2">
                  <strong>Created:</strong> {new Date(event.created_at).toLocaleString()}
                </p>
              </div>
              <div className="col-md-6">
                <p className="mb-0">
                  <strong>Updated:</strong> {new Date(event.updated_at).toLocaleString()}
                </p>
              </div>
            </div>
            {hasData(event.event_timestamps) && (
              <>
                <hr />
                <h6 className="text-muted mt-2 mb-2">Event Timestamps</h6>
                {Object.entries(event.event_timestamps).map(([key, value]) => (
                  <p key={key} className="mb-1">
                    <strong>{key}:</strong> {value}
                  </p>
                ))}
              </>
            )}
          </div>
        </div> */}

        {/* Add Activity Form */}
        <AddActivityModal
          show={showActivityModal}
          onHide={() => {
            setShowActivityModal(false);
            setShouldPulseActivity(false);
          }}
          onSave={handleSaveActivity}
          eventId={event.id}
          event={event}
          shouldPulse={shouldPulseActivity}
        />

        {/* Activities Timeline */}
        {!isEditing && (
          <ActivityTimeline
          activities={activitiesData}
          loading={activitiesLoading}
          onRefresh={fetchActivities}
          onViewCall={handleViewCallFromActivity}
          onFetchActivityWithPayload={fetchActivityWithPayload}
          onAddActivity={!showActivityModal ? () => {
            setShowActivityModal(true);
            setShouldPulseActivity(true);
          } : null}
          title="Activities Timeline"
          emptyMessage="No activities yet"
          isAnkrAdmin={isAnkrAdmin}
        />)}

      </div>
    );
  };

  const renderActivitiesTab = () => {
    if (!event) return null;

    return (
      <div className="p-3">
        {/* Add Activity Form */}
        <AddActivityModal
          show={showActivityModal}
          onHide={() => {
            setShowActivityModal(false);
            setShouldPulseActivity(false);
          }}
          onSave={handleSaveActivity}
          eventId={event.id}
          event={event}
          shouldPulse={shouldPulseActivity}
        />

        <ActivityTimeline
          activities={activitiesData}
          loading={activitiesLoading}
          onRefresh={fetchActivities}
          onViewCall={handleViewCallFromActivity}
          onFetchActivityWithPayload={fetchActivityWithPayload}
          onAddActivity={!showActivityModal ? () => {
            setShowActivityModal(true);
            setShouldPulseActivity(true);
          } : null}
          title="Activities Timeline"
          emptyMessage="No activities yet"
          isAnkrAdmin={isAnkrAdmin}
        />
      </div>
    );
  };

  const renderTranscriptTab = () => {
    // Support both full voiceai_data and summary event_notes
    const transcript = event?.voiceai_data?.transcript || event?.event_notes?.transcript;
    const transcriptObject = event?.voiceai_data?.transcript_object || null;

    if (!event || !transcript) {
      return (
        <div className="p-3">
          <div className="text-center py-5">
            <i className="fas fa-file-alt fa-3x text-muted mb-3"></i>
            <p className="text-muted">No conversation transcript available for this event</p>
          </div>
        </div>
      );
    }

    return (
      <div className="p-3">
        <VoiceAITranscriptViewer
          transcript={transcript}
          transcriptObject={transcriptObject}
          isAnkrAdmin={isAnkrAdmin}
        />
      </div>
    );
  };

  const renderChannelDataTab = () => {
    if (!event) return null;

    // Permission check for Ankr Admins only
    if (!isAnkrAdmin) {
      return (
        <div className="d-flex justify-content-middle align-items-center p-3 m-3 text-muted" style={{minHeight:'50vh'}}>
              <i className="fas fa-lock me-3"></i>
              You do not have permission to access channel-specific data. This feature is restricted to Ankr administrators only.
        </div>
      );
    }

    const channelDataSections = [
      { key: 'voiceai_data', label: 'VoiceAI Data', icon: 'fa-robot', data: event.voiceai_data },
      { key: 'sms_data', label: 'SMS Data', icon: 'fa-comment-sms', data: event.sms_data },
      { key: 'email_data', label: 'Email Data', icon: 'fa-envelope', data: event.email_data },
      { key: 'ehr_data', label: 'EHR Data', icon: 'fa-hospital', data: event.ehr_data },
      { key: 'communication_data', label: 'Communication Data', icon: 'fa-comments', data: event.communication_data },
    ];

    const hasAnyChannelData = channelDataSections.some(section => hasData(section.data));

    if (!hasAnyChannelData) {
      return (
        <div className="p-3">
          <div className="text-center py-5">
            <i className="fas fa-database fa-3x text-muted mb-3"></i>
            <p className="text-muted">No channel-specific data available</p>
          </div>
        </div>
      );
    }

    return (
      <div className="p-3">
        {channelDataSections.map(section => (
          hasData(section.data) && (
            <div key={section.key} className="card mb-3">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 className="card-title text-primary mb-0">
                    <i className={`fas ${section.icon} me-2`}></i>
                    {section.label}
                  </h6>
                  <button
                    className="btn btn-sm btn-outline-primary"
                    onClick={() => copyToClipboard(JSON.stringify(section.data, null, 2), section.label)}
                  >
                    <i className={`fas ${copied === section.label ? 'fa-check' : 'fa-copy'} me-1`}></i>
                    {copied === section.label ? 'Copied!' : 'Copy'}
                  </button>
                </div>
                <pre className="bg-light p-3 rounded" style={{ maxHeight: '400px', overflow: 'auto' }}>
                  {JSON.stringify(section.data, null, 2)}
                </pre>
              </div>
            </div>
          )
        ))}
      </div>
    );
  };

  const renderRawDataTab = () => {
    if (!event) return null;

    // Permission check for Ankr Admins only
    if (!isAnkrAdmin) {
      return (
        <div className="d-flex justify-content-middle align-items-center p-3 m-3 text-muted" style={{minHeight:'50vh'}}>
              <i className="fas fa-lock me-3"></i>
              You do not have permission to access raw event data. This feature is restricted to Ankr administrators only.
        </div>
      );
    }

    return (
      <div className="p-3">
        <div className="card">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="card-title text-primary mb-0">
                Raw JSON Data
              </h6>
              <button
                className="btn btn-sm btn-outline-primary"
                onClick={() => copyToClipboard(JSON.stringify(event, null, 2), 'JSON')}
              >
                <i className={`fas ${copied === 'JSON' ? 'fa-check' : 'fa-copy'} me-1`}></i>
                {copied === 'JSON' ? 'Copied!' : 'Copy'}
              </button>
            </div>
            <pre className="bg-dark text-light p-3 rounded" style={{ maxHeight: '600px', overflow: 'auto' }}>
              {JSON.stringify(event, null, 2)}
            </pre>
          </div>
        </div>

        {hasData(event.event_metadata) && (
          <div className="card mt-3">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="card-title text-primary mb-0">
                  Event Metadata
                </h6>
                <button
                  className="btn btn-sm btn-outline-primary"
                  onClick={() => copyToClipboard(JSON.stringify(event.event_metadata, null, 2), 'Metadata')}
                >
                  <i className={`fas ${copied === 'Metadata' ? 'fa-check' : 'fa-copy'} me-1`}></i>
                  {copied === 'Metadata' ? 'Copied!' : 'Copy'}
                </button>
              </div>
              <pre className="bg-dark text-light p-3 rounded" style={{ maxHeight: '400px', overflow: 'auto' }}>
                {JSON.stringify(event.event_metadata, null, 2)}
              </pre>
            </div>
          </div>
        )}
      </div>
    );
  };

  const tabs = [
    { id: 'summary', label: 'Summary', icon: 'fa-info-circle' },
    { id: 'activities', label: 'Activities', icon: 'fa-tasks' },
    ...(event?.voiceai_provider_event_id || event?.voiceai_data?.transcript ? [{ id: 'transcript', label: 'Transcript', icon: 'fa-file-alt' }] : []),
    { id: 'channel-data', label: 'Channel Data', icon: 'fa-database' },
    { id: 'raw', label: 'Raw Data', icon: 'fa-code' }
  ];

  const canNavigatePrev = onNavigate && currentIndex > 0;
  const canNavigateNext = onNavigate && currentIndex >= 0 && currentIndex < totalEvents - 1;

  const renderTitle = () => (
    <div className="d-flex align-items-center justify-content-between w-100">
      VCX Event Details
      {onNavigate && totalEvents > 0 && (
        <div className="d-flex align-items-center gap-2 ms-3">
          <span className="text-muted small">
            {currentIndex + 1} of {totalEvents}
          </span>
          <div className="btn-group btn-group-sm">
            <button
              className="btn btn-outline-secondary"
              onClick={() => handleNavigate('prev')}
              disabled={!canNavigatePrev || isNavigating}
              title="Previous Event"
            >
              <i className="fas fa-chevron-left"></i>
            </button>
            <button
              className="btn btn-outline-secondary"
              onClick={() => handleNavigate('next')}
              disabled={!canNavigateNext || isNavigating}
              title="Next Event"
            >
              <i className="fas fa-chevron-right"></i>
            </button>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <>
      <SidebarPanel
        isOpen={isOpen}
        onClose={handleClose}
        title={renderTitle()}
        style={{ width: '800px', maxWidth: '95vw' }}
        disableAnimation={true}
      >
        {/* Tabs */}
        <ul className="nav nav-tabs px-3 pt-3" role="tablist">
          {tabs.map(tab => (
            <li key={tab.id} className="nav-item" role="presentation">
              <button
                className={`nav-link ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => handleTabChange(tab.id)}
                type="button"
                role="tab"
              >
                <i className={`fas ${tab.icon} me-1`}></i>
                {tab.label}
              </button>
            </li>
          ))}
        </ul>

        {/* Tab Content */}
        <div className="tab-content">
          {activeTab === 'summary' && renderSummaryTab()}
          {activeTab === 'activities' && renderActivitiesTab()}
          {activeTab === 'transcript' && renderTranscriptTab()}
          {activeTab === 'channel-data' && renderChannelDataTab()}
          {activeTab === 'raw' && renderRawDataTab()}
        </div>
      </SidebarPanel>

      {/* Call Transcript Viewer */}
      <RetellCallViewer
        isOpen={viewerOpen}
        onClose={() => setViewerOpen(false)}
        callId={selectedCallId}
        callData={event?.voiceai_data}
      />
    </>
  );
};

export default VCXEventDetailPanel;
